/**
 * @file React Query hooks для in-app уведомлений
 * Polling каждые 30 секунд для unread count
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

export interface AppNotification {
  _id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  entityType?: string;
  entityId?: string;
  read: boolean;
  createdAt: string;
  readAt?: string;
}

const notifKeys = {
  all: ['app-notifications'] as const,
  list: () => [...notifKeys.all, 'list'] as const,
  count: () => [...notifKeys.all, 'count'] as const,
};

/** Список последних 20 уведомлений */
export function useNotifications() {
  return useQuery<AppNotification[]>({
    queryKey: notifKeys.list(),
    queryFn: async () => {
      const { data } = await api.get('/app-notifications?limit=20');
      return data.data || [];
    },
  });
}

/** Количество непрочитанных (polling 30s) */
export function useUnreadCount() {
  return useQuery<number>({
    queryKey: notifKeys.count(),
    queryFn: async () => {
      const { data } = await api.get('/app-notifications/count');
      return data.count || 0;
    },
    refetchInterval: 30_000,
  });
}

/** Отметить одно уведомление прочитанным */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/app-notifications/${id}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifKeys.all });
    },
  });
}

/** Отметить все уведомления прочитанными */
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.patch('/app-notifications/read-all');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifKeys.all });
    },
  });
}
